import { InputFieldType, formFieldError } from "@/types";
import * as React from "react";

import { BORDER_RADIUS } from "@/constants";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import { Box, InputLabel, Tooltip, Typography, useTheme } from "@mui/material";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import InputBase from "@mui/material/InputBase";
import Paper from "@mui/material/Paper";
import {
  FieldValues,
  UseControllerProps,
  useController,
} from "react-hook-form";
import { secondaryStyles } from "../secondaryStyles";

export default function CustomFileInputSecondary<T extends FieldValues>({
  control,
  name,
  error,
  inputProps = {},
  handleUpload,
  type = "primary",
  disabled,
  disableUpload,
  label,
  required,
  placeHolder,
  subText,
  isUpload = true,
  uploadButtonTooltip = "Upload",
  disabledTooltip,
}: CustomFileInputProps<T>): ReturnType<React.FC> {
  const theme = useTheme();
  const { field } = useController({
    control,
    name,
  });

  const handleOnChange = (e: React.ChangeEvent) => {
    field.onChange((e?.target as HTMLInputElement)?.files?.[0]);
  };

  const paperContent = (
    <Paper
      elevation={0}
      sx={{
        p: "2px 4px",
        display: "flex",
        alignItems: "center",
        mt: "12px",
        width: "100%",
        borderRadius: BORDER_RADIUS,
        ...secondaryStyles(type, theme),
        opacity: disabled ? 0.7 : 1,
        cursor: disabled ? "not-allowed" : "auto",
      }}
    >
        <input
          type="file"
          id="fileUpload"
          name="fileUpload"
          onChange={handleOnChange}
          style={{ display: "none" }}
          disabled={disabled}
          {...inputProps}
        />
        <InputBase
          sx={{
            ml: 1,
            flex: 1,
            ".MuiInputBase-input": {
              backgroundColor:
                type === "secondary"
                  ? "background.default"
                  : "background.paper",
              cursor: disabled ? "not-allowed" : "text",
              opacity: disabled ? 0.6 : 1,
            },
          }}
          inputProps={{ "aria-label": "search google maps" }}
          value={field?.value?.name ?? placeHolder ?? "Upload a file"}
          readOnly
          disabled={disabled}
        />
        <Tooltip title={disabled ? "" : "Select"}>
          <span>
            <IconButton
              sx={{ p: "10px" }}
              component="label"
              htmlFor="fileUpload"
              aria-label="select"
              disabled={disabled}
            >
              <FolderOpenIcon />
            </IconButton>
          </span>
        </Tooltip>
        {isUpload && (
          <>
            <Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />
            <Tooltip title={(disableUpload || disabled) ? "" : uploadButtonTooltip}>
              <span>
                <IconButton
                  color="primary"
                  sx={{ p: "10px" }}
                  aria-label="upload"
                  disabled={disableUpload || disabled}
                  onClick={handleUpload}
                >
                  <FileUploadIcon />
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}
    </Paper>
  );

  return (
    <Box>
      {label && (
        <InputLabel
          shrink
          htmlFor="fileUpload"
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          {label}
        </InputLabel>
      )}
      {disabled && disabledTooltip ? (
        <Tooltip title={disabledTooltip} placement="top-end">
          <span style={{ display: "block" }}>{paperContent}</span>
        </Tooltip>
      ) : (
        paperContent
      )}
      {subText && (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: 1,
            ml: 1,
            color: "text.secondary",
          }}
        >
          {subText}
        </Typography>
      )}
    </Box>
  );
}
interface CustomFileInputProps<T extends FieldValues = FieldValues>
  extends UseControllerProps<T> {
  error?: formFieldError;
  inputProps?: React.HTMLProps<HTMLInputElement>;
  handleUpload: () => void;
  type?: InputFieldType;
  disabled?: boolean;
  disableUpload?: boolean;
  label?: string;
  required?: boolean;
  placeHolder?: string;
  isUpload?: boolean;
  subText?: string;
  uploadButtonTooltip?: string;
  disabledTooltip?: string;
}
